import { getLocale } from '@tov-plus/ui';
import { formatEnumLabel, msg, TovCard, TovIcon } from '@tov-plus/ui';
import { CachedDataNotice } from '@tov-plus/pwa';
import { startTransition, useEffect, useState } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { useGuestLiveUpdates } from '../lib/communications/useGuestLiveUpdates';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import {
  readGuestSnapshot,
  writeGuestSnapshot,
} from '../lib/offline/cacheSnapshot';
function buildEventSummary(event: {
  guestSummary: string | null;
  description: string | null;
  startsAt: string | null;
  locationName: string | null;
}) {
  if (event.guestSummary) {
    return event.guestSummary;
  }
  if (event.description) {
    return event.description;
  }
  if (event.startsAt && event.locationName) {
    return 'Timing and location are confirmed for this event. Use the schedule for the latest guest-safe timing and RSVP for attendance details.';
  }
  if (event.startsAt) {
    return 'The hosts have confirmed the timing for this event. More guest-facing details may still be on the way.';
  }
  if (event.locationName) {
    return 'The hosts have published the location for this event. Timing and additional guest notes may still be updated.';
  }
  return 'This event is included in your invite, but the hosts have not published the guest-facing summary yet. Check schedule and RSVP for the latest confirmed details.';
}
function buildEventLocationLabel(event: {
  locationName: string | null;
  startsAt: string | null;
}) {
  if (event.locationName) {
    return event.locationName;
  }
  if (event.startsAt) {
    return 'Venue details will be shared before the event starts.';
  }
  return 'Location details have not been published for guests yet.';
}
function getWebsiteSectionSummary(contentPayload: Record<string, unknown>) {
  const candidates = [
    contentPayload.subtitle,
    contentPayload.summary,
    contentPayload.description,
    contentPayload.body,
  ];
  const summary = candidates.find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.trim().length > 0,
  );
  return (
    summary ?? 'Hosts added more published guest guidance to this section.'
  );
}

function formatCalendarDate(value: string) {
  return new Date(value).toISOString().replaceAll('-', '').replaceAll(':', '');
}

function buildGoogleCalendarUrl(event: {
  displayName: string;
  startsAt: string;
  endsAt: string | null;
  locationName: string | null;
  locationAddress: string | null;
  guestSummary: string | null;
  description: string | null;
}) {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.displayName,
    dates: `${formatCalendarDate(event.startsAt)}/${formatCalendarDate(
      event.endsAt ?? event.startsAt,
    )}`,
  });
  const location = [event.locationName, event.locationAddress]
    .filter(Boolean)
    .join(', ');

  if (location) {
    params.set('location', location);
  }
  if (event.guestSummary || event.description) {
    params.set('details', event.guestSummary ?? event.description ?? '');
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
export function EventsPage() {
  const access = useGuestAccess();
  const liveUpdates = useGuestLiveUpdates(access);
  const [routeSyncAt, setRouteSyncAt] = useState<string | null>(null);
  const [offlineNotice, setOfflineNotice] = useState<string | null>(null);
  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }
    const cacheKey = `tov-plus:guest-events:${access.occasionSlug}:${access.guestToken}`;
    async function syncRouteState() {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const cached = await readGuestSnapshot<{
          cachedAt: string;
        }>(cacheKey);
        startTransition(() => {
          setRouteSyncAt(cached?.cachedAt ?? null);
          setOfflineNotice(
            cached?.cachedAt
              ? msg(
                  'pages.events_page.offline_mode_event_summaries_may_lag_behind_the_latest_planner_updates',
                )
              : msg(
                  'pages.events_page.offline_mode_no_cached_event_freshness_marker_is_available_yet',
                ),
          );
        });
        return;
      }
      const cachedAt = new Date().toISOString();
      await writeGuestSnapshot(cacheKey, { cachedAt });
      startTransition(() => {
        setRouteSyncAt(cachedAt);
        setOfflineNotice(null);
      });
    }
    void syncRouteState();
  }, [access]);
  if (access.status !== 'ready') {
    return null;
  }
  const resolvedLocaleCode = getLocale();
  const occasionTimezone = access.experience.occasion.timezone;
  if (access.experience.events.length === 0) {
    return (
      <StatePanel
        preset="empty"
        actions={
          <NavLink className="secondary-link" to="/">
            {msg('general.navigation.return_to_overview')}{' '}
          </NavLink>
        }
      />
    );
  }
  const overrideByEventId = new Map(
    access.experience.eventVisualOverrides.map((override) => [
      override.event_id,
      override,
    ]),
  );
  const updateCountByEventId = new Map<string, number>();
  const now = Date.now();
  const eventDayEvents = access.experience.events.filter((event) => {
    if (!event.startsAt) {
      return false;
    }
    const delta = Date.parse(event.startsAt) - now;
    return delta >= -1000 * 60 * 60 * 12 && delta <= 1000 * 60 * 60 * 36;
  });
  const publishedWebsiteSections =
    access.experience.guestWebsite.sections.filter(
      (section) =>
        section.enabled &&
        [
          'faq',
          'accommodations',
          'maps',
          'countdown',
          'important_notes',
          'travel',
          'contact_help',
        ].includes(section.type),
    );
  for (const communication of access.experience.recentCommunications) {
    if (!communication.event) {
      continue;
    }
    updateCountByEventId.set(
      communication.event.id,
      (updateCountByEventId.get(communication.event.id) ?? 0) + 1,
    );
  }
  return (
    <section className="page-grid">
      <TovCard variant="content" as="div" isWide className="hero-card">
        <p className="page-eyebrow">{msg('general.modules.events.name')}</p>
        <h2>
          {msg('pages.events_page.pages_events_page_your_occasion_events')}
        </h2>
        <p>
          {msg(
            'pages.events_page.pages_events_page_this_page_only_shows_the_event_summaries_that',
          )}{' '}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>{access.experience.events.length}</strong>
            <span>
              {msg('pages.events_page.pages_events_page_visible_event')}
            </span>
          </article>
          <article>
            <strong>
              {
                access.experience.events.filter((event) =>
                  updateCountByEventId.has(event.id),
                ).length
              }
            </strong>
            <span>
              {msg(
                'pages.events_page.pages_events_page_event_with_recent_update',
              )}
            </span>
          </article>
          <article>
            <strong>{access.experience.recentCommunications.length}</strong>
            <span>
              {msg('pages.events_page.pages_events_page_visible_message')}
            </span>
          </article>
        </div>
        <div className="action-row">
          <NavLink className="secondary-link" to="/today">
            {msg('pages.home_page.pages_home_page_event_day_view')}{' '}
          </NavLink>
          <NavLink className="primary-button" to="/rsvp">
            {msg('app.guest_workflows.review_rsvp')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/schedule">
            {`${msg('general.actions.open')} ${msg('general.modules.schedule.name')}`}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/seating">
            {msg('pages.events_page.pages_events_page_check_seating')}{' '}
          </NavLink>
        </div>
        {routeSyncAt ? (
          <p>
            {msg('pages.events_page.pages_events_page_last_synced')}{' '}
            {formatGuestDateTime(
              routeSyncAt,
              resolvedLocaleCode,
              occasionTimezone,
            )}
            {offlineNotice ? ` • ${offlineNotice}` : ''}
          </p>
        ) : offlineNotice ? (
          <p>{offlineNotice}</p>
        ) : null}
        <CachedDataNotice cachedValue={access.experience} />
        {liveUpdates.unreadCount > 0 ? (
          <p>
            {liveUpdates.unreadCount}{' '}
            {msg('pages.events_page.pages_events_page_new_planner_update')}{' '}
            {liveUpdates.unreadCount === 1 ? '' : 's'}{' '}
            {msg(
              'pages.events_page.pages_events_page_are_still_unread_in_the_message_center',
            )}{' '}
          </p>
        ) : null}
        {eventDayEvents.length > 0 ? (
          <div className="detail-list">
            <article>
              <strong>
                {msg('pages.events_page.pages_events_page_event_day_watch')}
              </strong>
              <span>
                {eventDayEvents.length} {msg('general.fields.event.label')}{' '}
                {eventDayEvents.length === 1 ? '' : 's'}{' '}
                {msg(
                  'pages.events_page.pages_events_page_are_in_the_event_day_window_check_schedule',
                )}{' '}
              </span>
            </article>
          </div>
        ) : null}
      </TovCard>

      {publishedWebsiteSections.length > 0 ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg(
              'pages.events_page.pages_events_page_published_guest_guidance',
            )}
          </p>
          <div className="detail-list">
            {publishedWebsiteSections.map((section) => (
              <article key={section.id}>
                <strong>
                  {section.anchor_label ??
                    (typeof section.content_payload.title === 'string'
                      ? section.content_payload.title
                      : section.type.replaceAll('_', ' '))}
                </strong>
                <span>{getWebsiteSectionSummary(section.content_payload)}</span>
                <span>
                  {section.type.replaceAll('_', ' ')} •{' '}
                  {section.linked_event_id
                    ? msg('pages.events_page.pages_events_page_event_linked')
                    : msg(
                        'pages.gallery_page.pages_gallery_page_occasion_wide',
                      )}
                </span>
                <NavLink
                  className="secondary-link"
                  to={
                    section.type === 'maps' ||
                    section.type === 'travel' ||
                    section.type === 'accommodations'
                      ? '/locations'
                      : section.type === 'contact_help'
                        ? '/help'
                        : '/'
                  }
                >
                  {msg('general.actions.open')}{' '}
                </NavLink>
              </article>
            ))}
          </div>
        </TovCard>
      ) : null}

      {access.experience.events.map((event) => {
        const override = overrideByEventId.get(event.id);
        const updateCount = updateCountByEventId.get(event.id) ?? 0;
        const hasUnreadEventUpdate = liveUpdates.hasUnreadEventUpdate(event.id);
        const eventDay =
          event.startsAt !== null &&
          Date.parse(event.startsAt) - now >= -1000 * 60 * 60 * 12 &&
          Date.parse(event.startsAt) - now <= 1000 * 60 * 60 * 36;
        const accent =
          override?.event_accent_color ??
          access.experience.designTheme?.tokens.accentStrong ??
          undefined;
        const eventDateLabel = event.startsAt
          ? formatGuestDateTime(
              event.startsAt,
              getLocale(),
              event.timezone ?? access.experience.occasion.timezone,
            )
          : msg('pages.events_page.pages_events_page_time_not_published_yet');
        const eventLocationLabel = buildEventLocationLabel(event);
        const mapHref = event.locationAddress
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              event.locationAddress,
            )}`
          : null;
        const calendarHref = event.startsAt
          ? buildGoogleCalendarUrl({
              displayName: event.displayName,
              startsAt: event.startsAt,
              endsAt: event.endsAt,
              locationName: event.locationName,
              locationAddress: event.locationAddress,
              guestSummary: event.guestSummary,
              description: event.description,
            })
          : null;
        return (
          <article
            key={event.id}
            className="event-card"
            style={
              accent
                ? {
                    borderColor: accent,
                    boxShadow: `0 8px 24px color-mix(in srgb, ${accent} 20%, transparent)`,
                  }
                : undefined
            }
          >
            <div className="event-card__header">
              <div>
                <p
                  className="event-card__status"
                  style={accent ? { color: accent } : undefined}
                >
                  {formatEnumLabel(event.status)}
                </p>
                <h3>{event.displayName}</h3>
              </div>
              {eventDay ? (
                <span className="event-card__pill">
                  <TovIcon name="clock" />
                  {msg('general.calendar.today')}
                </span>
              ) : null}
            </div>
            <div className="event-card__facts">
              <span>
                <TovIcon name="calendar-blank" />
                {eventDateLabel}
              </span>
              <span>
                <TovIcon name="map-pin" />
                {eventLocationLabel}
              </span>
            </div>
            <p className="event-card__summary">{buildEventSummary(event)}</p>
            {hasUnreadEventUpdate || updateCount > 0 ? (
              <p className="event-card__update">
                <TovIcon name="megaphone" />
                {hasUnreadEventUpdate
                  ? msg(
                      'pages.events_page.pages_events_page_unread_planner_update',
                    )
                  : msg(
                      'pages.events_page.pages_events_page_recent_change_published',
                    )}
              </p>
            ) : null}
            <div
              className="event-card__actions"
              role="group"
              aria-label={event.displayName}
            >
              <NavLink
                className="event-card__action event-card__action--primary"
                to={`/events/${event.id}`}
              >
                <TovIcon name="arrow-right" />
                {msg('pages.event_detail_page.event_details')}{' '}
              </NavLink>
              {mapHref ? (
                <a className="event-card__action" href={mapHref}>
                  <TovIcon name="map-pin" />
                  {msg(
                    'pages.locations_page.pages_locations_page_open_directions',
                  )}
                </a>
              ) : (
                <NavLink className="event-card__action" to="/locations">
                  <TovIcon name="map-pin" />
                  {msg(
                    'pages.today_page.pages_today_page_travel_guidance',
                  )}{' '}
                </NavLink>
              )}
              {calendarHref ? (
                <a className="event-card__action" href={calendarHref}>
                  <TovIcon name="calendar-blank" />
                  {msg('pages.guest_home_page_sections.add_to_calendar')}{' '}
                </a>
              ) : (
                <NavLink className="event-card__action" to="/schedule">
                  <TovIcon name="calendar-blank" />
                  {msg(
                    'pages.events_page.pages_events_page_check_schedule',
                  )}{' '}
                </NavLink>
              )}
              <NavLink
                className="event-card__action"
                to={`/events/${event.id}`}
              >
                <TovIcon name="scroll" />
                {msg('pages.events_page.pages_events_page_dress_code')}
              </NavLink>
              <NavLink className="event-card__action" to="/help">
                <TovIcon name="lifebuoy" />
                {msg('app.router.app_router_contact_support')}{' '}
              </NavLink>
            </div>
          </article>
        );
      })}
    </section>
  );
}
